import { blank, cell, columnsOf, type SheetTable } from './file';

/**
 * What a sheet's columns hold, matched by name as the phone matches them (lib/services/orders/sheet_columns.dart):
 * the same fields, the same header lists and the same rules, so a sheet reads the same on the web and on the phone.
 * A column that matches nothing goes to the stop's notes ([SheetColumns.withNotes]); custom fields will add their
 * own entries here.
 */
export type SheetField =
  | 'name'
  | 'phone'
  | 'address'
  | 'area'
  | 'landmark'
  | 'pin'
  | 'latitude'
  | 'longitude'
  | 'packages'
  | 'size'
  | 'deliverBy'
  | 'type'
  | 'orderNumber'
  | 'amount'
  | 'total'
  | 'fee'
  | 'notes'
  | 'asNote'
  | 'skip';

export const FIELD_LABELS: Record<SheetField, string> = {
  name: 'Customer name',
  phone: 'Phone',
  address: 'Address',
  area: 'Area or city',
  landmark: 'Landmark',
  pin: 'Pin',
  latitude: 'Latitude',
  longitude: 'Longitude',
  packages: 'Packages',
  size: 'Size',
  deliverBy: 'Deliver by',
  type: 'Pickup or delivery',
  orderNumber: 'Order number',
  amount: 'Order amount',
  total: 'Total to collect',
  fee: 'Delivery fee',
  notes: 'Notes',
  asNote: 'Add to notes',
  skip: 'Leave out',
};
export const FIELDS = Object.keys(FIELD_LABELS) as SheetField[];

/** Each field's headers, as people write them (lower case, words only). */
const HEADERS: Record<SheetField, string[]> = {
  name: [
    'name', 'names', 'full name', 'customer', 'customer name', 'client', 'client name', 'recipient', 'recipient name',
    'receiver', 'receiver name', 'consignee', 'consignee name', 'buyer', 'buyer name', 'contact name', 'contact person',
    'first name', 'last name', 'surname', 'deliver to name', 'ship to name', 'shipping name', 'attention',
  ],
  phone: [
    'phone', 'phones', 'phone no', 'phone num', 'phone number', 'phone numbers', 'telephone', 'telephone number', 'tel', 'tel no', 'mobile',
    'mobile no', 'mobile number', 'gsm', 'gsm number', 'cell', 'whatsapp', 'whatsapp no', 'whatsapp number', 'contact',
    'contact no', 'contact number', 'customer phone', 'customer number', 'customer phone number', 'recipient phone',
    'recipient number', 'receiver phone', 'receiver number', 'phone 2', 'alternative phone', 'alternate phone', 'other phone',
  ],
  address: [
    'address', 'addresses', 'addr', 'delivery address', 'customer address', 'recipient address', 'receiver address',
    'shipping address', 'home address', 'house address', 'office address', 'street', 'street address', 'address line 1',
    'address line 2', 'address 1', 'address 2', 'address1', 'address2', 'shipping address1', 'shipping address2',
    'shipping street', 'location', 'delivery location', 'destination', 'drop off', 'dropoff', 'drop off address',
    'drop off location', 'deliver to', 'send to', 'ship to', 'stop address',
  ],
  area: [
    'area', 'city', 'town', 'state', 'estate', 'neighbourhood', 'neighborhood', 'district', 'region', 'province', 'lga',
    'shipping city', 'shipping province', 'delivery area', 'delivery city', 'delivery state', 'zone',
  ],
  landmark: [
    'landmark', 'landmarks', 'nearest landmark', 'near', 'directions', 'direction', 'nearest bus stop', 'bus stop', 'busstop',
    'gate code', 'gate', 'access', 'access code', 'access instructions', 'how to get in', 'floor', 'apartment', 'flat',
  ],
  pin: [
    'pin', 'map pin', 'maps pin', 'location pin', 'maps link', 'map link', 'google maps', 'google maps link', 'location link',
    'plus code', 'pluscode', 'plus codes', 'coordinates', 'gps', 'gps coordinates', 'lat lng', 'lat long', 'latlng',
  ],
  latitude: ['latitude', 'lat'],
  longitude: ['longitude', 'lng', 'lon', 'long'],
  packages: [
    'packages', 'package', 'no of packages', 'number of packages', 'package count', 'pkgs', 'parcels', 'parcel count', 'no of parcels',
    'qty', 'quantity', 'no of items', 'number of items', 'item count', 'items count', 'pieces', 'pcs', 'cartons', 'units',
  ],
  size: ['size', 'parcel size', 'package size', 'packages size'],
  deliverBy: [
    'deliver by', 'delivery by', 'deliver before', 'deadline', 'delivery deadline', 'due', 'due by', 'due time', 'latest',
    'latest by', 'by', 'before', 'delivery time', 'preferred time',
  ],
  type: ['pickup or delivery', 'delivery or pickup', 'pick up or delivery', 'pickup delivery', 'type', 'stop type', 'order type', 'job type'],
  orderNumber: [
    'order number', 'order numbers', 'order no', 'order num', 'order id', 'order', 'order ref', 'reference', 'reference number', 'ref', 'ref no',
    'tracking number', 'tracking no', 'tracking', 'waybill', 'waybill number', 'invoice', 'invoice no', 'invoice number',
  ],
  amount: ['order amount', 'order value', 'amount', 'price', 'item price', 'order price', 'value', 'goods value', 'cost'],
  total: [
    'amount to collect', 'amount to collect on delivery', 'to collect', 'collect', 'total to collect', 'total', 'total amount', 'order total',
    'cod', 'cod amount', 'cash on delivery', 'pay on delivery', 'payment on delivery', 'amount due', 'balance', 'balance due',
  ],
  fee: [
    'delivery fee', 'delivery charge', 'delivery charges', 'delivery cost', 'dispatch fee', 'logistics', 'logistics fee',
    'shipping fee', 'shipping cost', 'fee', 'courier fee',
  ],
  notes: [
    'notes', 'note', 'comment', 'comments', 'remark', 'remarks', 'instruction', 'instructions', 'special instruction',
    'special instructions', 'delivery instruction', 'delivery instructions', 'delivery note', 'delivery notes',
  ],
  asNote: [],
  skip: [
    'email', 'e mail', 'email address', 'customer email', 'sku', 'country', 'country code', 'postal code', 'post code', 'postcode', 'zip', 'zip code',
    'date', 'order date', 'created at', 'created', 'updated at', 'sender', 'sender name', 'sender phone', 's n', 'sn', 'no', 'serial', 'serial number', '', 'what happened', 'status',
  ],
};

const BY_HEADER = new Map<string, SheetField>(FIELDS.flatMap((f) => HEADERS[f].map((h) => [h, f] as [string, SheetField])));

/** A header reduced to its words: "Customer's Phone No.*" → "customer phone no". */
export const plainHeader = (h: string) =>
  h
    .toLowerCase()
    .replace(/['’]s\b/g, '')
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** A word in a header that says what the column is, for headers no one's list has: "Delivery Contact Mobile". */
const HINTS: Record<string, SheetField> = {
  address: 'address',
  landmark: 'landmark',
  phone: 'phone',
  mobile: 'phone',
  gsm: 'phone',
  whatsapp: 'phone',
  name: 'name',
  notes: 'notes',
  note: 'notes',
  instructions: 'notes',
  packages: 'packages',
  email: 'skip',
};

/** Words that say a column is about someone or something other than the stop's customer: asked about, not guessed. */
const NOT_THE_CUSTOMER = new Set([
  'vendor', 'sender', 'seller', 'merchant', 'shop', 'store', 'supplier', 'product', 'item', 'items', 'rider', 'driver', 'courier',
  'dispatcher', 'staff', 'agent', 'company', 'business', 'pickup', 'pick', 'billing', 'return', 'warehouse', 'from',
]);

/** What [header] means; null when nothing says. */
export function fieldOfHeader(header: string): SheetField | null {
  const plain = plainHeader(header);
  const known = BY_HEADER.get(plain);
  if (known) return known;
  const words = plain.split(' ');
  if (words.some((w) => NOT_THE_CUSTOMER.has(w))) return null;
  for (const w of words) if (HINTS[w]) return HINTS[w];
  return null;
}

const letter = (column: number) => {
  let n = column + 1;
  let out = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

/** How a sheet's columns are read: which row has the headers and what each column holds (one entry per column). */
export interface SheetColumns {
  /** Index into the table's rows of the header row; null when the sheet has none. */
  headerRow: number | null;
  /** Each column's name in the sheet; "Column A" where it has none. */
  headers: string[];
  /** What each column holds; null where nothing says yet. */
  fields: (SheetField | null)[];
  /** [fields] came from the last time this layout was matched. */
  remembered?: boolean;
}

/** Whether a row can be placed: an address, or somewhere to read a pin. */
export const canRead = (c: SheetColumns) => c.fields.includes('address') || c.fields.includes('pin') || (c.fields.includes('latitude') && c.fields.includes('longitude'));

/** Nothing to ask: goes straight to the stops. */
export const settled = (c: SheetColumns) => c.headerRow != null && canRead(c) && !c.fields.includes(null);

/** The layout, for remembering: the same headers in the same order. */
export const layoutOf = (c: SheetColumns) => (c.headerRow == null ? `no-headers:${c.fields.length}` : c.headers.map(plainHeader).join('|'));

/** Where a column's values start, and the rows that hold stops. */
export const firstData = (c: SheetColumns) => (c.headerRow == null ? 0 : c.headerRow + 1);

export const withField = (c: SheetColumns, column: number, field: SheetField): SheetColumns => ({ ...c, remembered: false, fields: c.fields.map((f, i) => (i === column ? field : f)) });

/** Where nothing says yet, a column's text goes to the notes, so nothing in the sheet is lost unless someone says so. */
export const withNotes = (c: SheetColumns): SheetColumns => ({ ...c, fields: c.fields.map((f) => f ?? 'asNote') });

/** A column with no header: left out when it's empty all the way down. */
function emptyHeader(t: SheetTable, headerRow: number, column: number): SheetField | null {
  for (let i = headerRow + 1; i < t.rows.length; i++) if (cell(t.rows[i], column).trim()) return null;
  return 'skip';
}

/** [t]'s columns with [headerRow] as the header row, or with none. */
export function columnsAt(t: SheetTable, headerRow: number | null): SheetColumns {
  const n = columnsOf(t);
  const head = headerRow == null ? null : t.rows[headerRow];
  return {
    headerRow,
    headers: Array.from({ length: n }, (_, c) => (head && cell(head, c).trim() ? cell(head, c).trim() : `Column ${letter(c)}`)),
    fields: Array.from({ length: n }, (_, c) => (head == null ? null : !cell(head, c).trim() ? emptyHeader(t, headerRow!, c) : fieldOfHeader(cell(head, c)))),
  };
}

/** Headers Routr doesn't know still look like headers: a first row of only words over rows with numbers in them. */
function plainHeaders(t: SheetTable): number | null {
  const first = t.rows.findIndex((r) => !blank(r));
  if (first < 0) return null;
  if (t.rows[first].cells.some((c) => /\d/.test(c))) return null;
  return t.rows.slice(first + 1, first + 6).some((r) => r.cells.some((c) => /\d/.test(c))) ? first : null;
}

/** The first row with anything in it: the one someone may say holds the column names, or doesn't. */
export const firstRow = (t: SheetTable) => {
  const i = t.rows.findIndex((r) => !blank(r));
  return i < 0 ? null : i;
};

/**
 * Reads [t]'s columns: the header row is the first of the top rows that names at least two columns (a title can sit
 * above it), or one that names the address. [remembered]: layouts matched before, by [layoutOf].
 */
export function matchColumns(t: SheetTable, remembered: Record<string, string[]> = {}): SheetColumns {
  let headerRow: number | null = null;
  for (let i = 0, looked = 0; i < t.rows.length && looked < 6; i++) {
    const row = t.rows[i];
    if (blank(row)) continue;
    looked++;
    const found = row.cells.filter((c) => c.trim()).map(fieldOfHeader).filter((f): f is SheetField => !!f && f !== 'skip');
    if (found.length >= 2 || found.includes('address')) {
      headerRow = i;
      break;
    }
  }
  headerRow ??= plainHeaders(t);
  const found = columnsAt(t, headerRow);
  const saved = remembered[layoutOf(found)];
  if (saved && saved.length === columnsOf(t)) {
    return { ...found, fields: saved.map((s) => (FIELDS.includes(s as SheetField) ? (s as SheetField) : 'asNote')), remembered: true };
  }
  return found;
}

/** A few of the column's values, to tell columns apart when matching. */
export function sample(c: SheetColumns, t: SheetTable, column: number): string {
  const seen: string[] = [];
  for (let i = firstData(c); i < t.rows.length && seen.length < 2; i++) {
    const v = cell(t.rows[i], column).trim();
    if (v && !seen.includes(v)) seen.push(v);
  }
  return seen.join(' · ');
}
